/**
 * Integer 2D vector helpers. Authoritative results are always rounded back
 * to integer plan units; floating point appears only inside a single
 * computation (distances, projections, intersections) and never persists.
 */
import type { Point } from "./model";

export function sub(a: Point, b: Point): Point {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function add(a: Point, b: Point): Point {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function cross(a: Point, b: Point): number {
  return a.x * b.y - a.y * b.x;
}

export function dot(a: Point, b: Point): number {
  return a.x * b.x + a.y * b.y;
}

export function length(a: Point): number {
  return Math.hypot(a.x, a.y);
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Exact integer squared length (safe for residential extents). */
export function lengthSquared(a: Point): number {
  return a.x * a.x + a.y * a.y;
}

/** Integer length of a segment, rounded half-up. */
export function segmentLength(a: Point, b: Point): number {
  return Math.round(distance(a, b));
}

export function pointsEqual(a: Point, b: Point): boolean {
  return a.x === b.x && a.y === b.y;
}

export function roundPoint(p: { x: number; y: number }): Point {
  return { x: Math.round(p.x), y: Math.round(p.y) };
}

/** Parameter t of the projection of p onto segment ab (unclamped). */
export function projectParam(p: Point, a: Point, b: Point): number {
  const ab = sub(b, a);
  const l2 = lengthSquared(ab);
  if (l2 === 0) return 0;
  return dot(sub(p, a), ab) / l2;
}

/** Closest point on segment ab to p (clamped), rounded to integers. */
export function closestPointOnSegment(p: Point, a: Point, b: Point): { point: Point; t: number; distance: number } {
  const t = Math.max(0, Math.min(1, projectParam(p, a, b)));
  const point = roundPoint({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  return { point, t, distance: distance(p, point) };
}

/** Point along a→b at distance d from a (rounded). */
export function pointAlong(a: Point, b: Point, d: number): Point {
  const len = distance(a, b);
  if (len === 0) return { ...a };
  return roundPoint({ x: a.x + ((b.x - a.x) * d) / len, y: a.y + ((b.y - a.y) * d) / len });
}

/** Unit direction a→b as floats (display / intermediate only). */
export function direction(a: Point, b: Point): { x: number; y: number } {
  const len = distance(a, b);
  if (len === 0) return { x: 1, y: 0 };
  return { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
}

/** Left-hand normal of a→b (unit, floats). */
export function leftNormal(a: Point, b: Point): { x: number; y: number } {
  const d = direction(a, b);
  return { x: -d.y, y: d.x };
}

/**
 * Proper intersection of segments p1p2 and p3p4 (interiors crossing).
 * Returns the rounded point and both parameters, or null when parallel,
 * collinear, or touching only at endpoints (tolerance `eps` in t-space).
 */
export function segmentIntersection(
  p1: Point,
  p2: Point,
  p3: Point,
  p4: Point,
): { point: Point; t: number; u: number } | null {
  const r = sub(p2, p1);
  const s = sub(p4, p3);
  const denom = cross(r, s);
  if (denom === 0) return null;
  const qp = sub(p3, p1);
  const t = cross(qp, s) / denom;
  const u = cross(qp, r) / denom;
  if (t <= 0 || t >= 1 || u <= 0 || u >= 1) return null;
  return { point: roundPoint({ x: p1.x + r.x * t, y: p1.y + r.y * t }), t, u };
}

/** Intersection of two infinite lines (through a1-a2 and b1-b2); null if parallel. */
export function lineIntersection(a1: Point, a2: Point, b1: Point, b2: Point): { x: number; y: number } | null {
  const r = sub(a2, a1);
  const s = sub(b2, b1);
  const denom = cross(r, s);
  if (denom === 0) return null;
  const t = cross(sub(b1, a1), s) / denom;
  return { x: a1.x + r.x * t, y: a1.y + r.y * t };
}

/** True when c lies on segment ab within `tolerance` pu (perpendicular distance and inside the span). */
export function pointOnSegment(c: Point, a: Point, b: Point, tolerance: number): boolean {
  const ab = sub(b, a);
  const l2 = lengthSquared(ab);
  if (l2 === 0) return distance(a, c) <= tolerance;
  const t = dot(sub(c, a), ab) / l2;
  if (t < 0 || t > 1) return false;
  const proj = { x: a.x + ab.x * t, y: a.y + ab.y * t };
  return distance(proj, c) <= tolerance;
}

/** Collinearity test for two directions (|sin| below tolerance). */
export function collinear(d1: Point, d2: Point, sinTolerance = 0.002): boolean {
  const l1 = length(d1);
  const l2 = length(d2);
  if (l1 === 0 || l2 === 0) return false;
  return Math.abs(cross(d1, d2)) / (l1 * l2) < sinTolerance;
}

/** Shoelace area × 2 in vertex-relative coordinates (exact integer); positive = CCW. */
export function twiceSignedArea(points: Point[]): number {
  if (points.length < 3) return 0;
  const ox = points[0].x;
  const oy = points[0].y;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    sum += (p.x - ox) * (q.y - oy) - (q.x - ox) * (p.y - oy);
  }
  return sum;
}

export function perimeterLength(points: Point[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    sum += distance(points[i], points[(i + 1) % points.length]);
  }
  return Math.round(sum);
}

/** Even-odd point-in-polygon. */
export function pointInPolygon(p: Point, polygon: readonly Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    const intersects = a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}
